import { useUserStore } from './userStore';
import { isArsenalSessionContext } from './sessionContext';
import toast from 'react-hot-toast';

const VERSION = import.meta.env.VITE_APP_VERSION || Date.now().toString();
const VERSION_KEY = 'app_version';
const LAST_CHECK_KEY = 'last_version_check';
const CHECK_INTERVAL = 15 * 1000; // 15 secondes pour une vérification plus fréquente

// Debug toggle: set window.__VERSION_DEBUG__ = true in console to re-enable logs
const VERSION_DEBUG = false;
const vLog = (...args: unknown[]) => {
  try {
    const win = typeof window !== 'undefined' ? (window as unknown as { __VERSION_DEBUG__?: boolean }) : undefined;
    if (VERSION_DEBUG || win?.__VERSION_DEBUG__) {
      console.log(...args);
    }
  } catch {
    /* no-op */
  }
};

declare global {
  interface Window {
    debugVersion?: typeof debugVersionSystem;
    forceVersionCheck?: typeof forceVersionCheck;
    forceReload?: typeof forceReload;
    testUpdateDetection?: () => void;
    clearVersionData?: () => void;
    setVersionDebug?: (enabled: boolean) => void;
    __VERSION_DEBUG__?: boolean;
  }
}

let versionCheckInterval: NodeJS.Timeout | null = null;
let updateModalCallback: ((show: boolean) => void) | null = null;

export function setUpdateModalCallback(callback: (show: boolean) => void) {
  updateModalCallback = callback;
}

export function checkVersion(): boolean {
  const lastVersion = localStorage.getItem(VERSION_KEY);

  // Si c'est la première visite (pas de version stockée), ne pas afficher le modal
  if (!lastVersion) {
    vLog(`Première visite détectée, initialisation de la version: ${VERSION}`);
    localStorage.setItem(VERSION_KEY, VERSION);
    localStorage.setItem(LAST_CHECK_KEY, Date.now().toString());
    return false; // Pas de modal pour la première visite
  }

  // Si c'est une nouvelle version (et pas la première visite)
  if (lastVersion !== VERSION) {
    vLog(`Nouvelle version détectée: ${VERSION} (ancienne: ${lastVersion})`);

    const remember = localStorage.getItem('remember_me') === 'true';
    // Une mise à jour conserve les sessions internes, y compris TSLH en embed.
    // Arsenal/OIDC garde son nettoyage historique ; remember_me reste inchangé.
    const preserveSession = remember || !isArsenalSessionContext();

    if (!preserveSession) {
      // Nettoyage complet (username/user_id/is_bpm_session) pour éviter un état
      // incohérent (ex: is_bpm_session resté true avec username null) qui empêche
      // AuthPage de détecter correctement qu'une reconnexion BPM est nécessaire.
      useUserStore.getState().clearUser();
    }

    // Nettoyage complet du localStorage sauf les clés utiles
    const keysToKeep = ['theme-storage'];
    if (preserveSession) {
      keysToKeep.push('user-storage', 'remember_me', 'league-storage');
    }

    const storage: Record<string, string> = {};
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) storage[key] = localStorage.getItem(key) as string;
    }

    localStorage.clear();

    // Restaurer seulement les clés importantes
    Object.keys(storage).forEach((key) => {
      if (keysToKeep.some((keepKey) => key.includes(keepKey))) {
        localStorage.setItem(key, storage[key]);
      }
    });

    // Mise à jour de la version dans le localStorage
    localStorage.setItem(VERSION_KEY, VERSION);
    localStorage.setItem(LAST_CHECK_KEY, Date.now().toString());

    return true;
  }

  return false;
}

export async function checkForNewVersion(): Promise<boolean> {
  try {
    // Vérifier si on a déjà vérifié récemment (réduire l'intervalle à 5 secondes)
    const lastCheck = localStorage.getItem(LAST_CHECK_KEY);
    if (lastCheck && (Date.now() - parseInt(lastCheck)) < 5000) {
  vLog('🕐 Vérification trop récente, attente...', Date.now() - parseInt(lastCheck));
      return false;
    }

    const timestamp = Date.now();
    const randomParam = Math.random().toString(36).substring(7);
    
  vLog('🔍 Début de la vérification de version...', new Date().toISOString());
    
    // Méthode 1: Vérifier le fichier HTML principal pour détecter les nouveaux hashes d'assets
    try {
  vLog('📄 Vérification du HTML principal...');
      const htmlResponse = await fetch(`/?v=${timestamp}&cb=${randomParam}`, {
        method: 'GET',
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });

      if (htmlResponse.ok) {
        const htmlContent = await htmlResponse.text();
        
        // Extraire les noms de fichiers JavaScript/CSS avec leurs hashes
        const assetMatches = htmlContent.match(/assets\/[^"'\s]+\.(js|css)/g);
        const currentAssets = assetMatches ? assetMatches.join(',') : '';
        
  vLog('📦 Assets actuels trouvés:', currentAssets);
        
        // Comparer avec les assets précédents
        const lastAssets = localStorage.getItem('app_assets');
  vLog('📦 Assets précédents:', lastAssets);
        
        // Ne montrer le modal que si on avait déjà des assets ET qu'ils sont différents
        if (lastAssets && lastAssets !== currentAssets && currentAssets) {
          vLog('🚨 NOUVEAUX ASSETS DÉTECTÉS!');
          vLog('📦 Nouveaux:', currentAssets);
          vLog('📦 Anciens:', lastAssets);
          localStorage.setItem('app_assets', currentAssets);
          showUpdateModal();
          return true;
        }
        
        // Sauvegarder les assets actuels si c'est la première fois (sans afficher le modal)
        if (currentAssets && !lastAssets) {
          vLog('💾 Première sauvegarde des assets (première visite):', currentAssets);
          localStorage.setItem('app_assets', currentAssets);
        }
      } else {
        console.warn('❌ Erreur HTTP lors de la récupération du HTML:', htmlResponse.status);
      }
    } catch (htmlError) {
      console.error('❌ Erreur lors de la vérification HTML:', htmlError);
    }

    // Méthode 2: Vérifier si on peut accéder aux anciens assets en cache
    try {
      const lastAssets = localStorage.getItem('app_assets');
      if (lastAssets) {
  vLog('🔗 Vérification de l\'existence des anciens assets...');
        // Tenter d'accéder à un ancien asset pour voir s'il existe encore
        const firstAsset = lastAssets.split(',')[0];
        if (firstAsset) {
          const assetResponse = await fetch(`/${firstAsset}?v=${timestamp}`, {
            method: 'HEAD',
            cache: 'no-cache'
          });
          
          vLog('📁 Status de l\'ancien asset:', assetResponse.status);
          
          // Si l'asset n'existe plus (404), c'est qu'il y a une nouvelle version
          if (!assetResponse.ok && assetResponse.status === 404) {
            vLog('🚨 ANCIEN ASSET NON TROUVÉ - NOUVELLE VERSION DÉTECTÉE!');
            showUpdateModal();
            return true;
          }
        }
      }
    } catch (assetError) {
      console.error('❌ Erreur lors de la vérification des assets:', assetError);
    }

    // Méthode 3: Vérifier le fichier version.json
    try {
  vLog('📋 Vérification du fichier version.json...');
      const versionResponse = await fetch(`/version.json?v=${timestamp}&cb=${randomParam}`, {
        method: 'GET',
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });

      if (versionResponse.ok) {
        const versionData = await versionResponse.json();
        const serverVersion = versionData.timestamp || versionData.version;
        
  vLog('🏷️ Version serveur:', serverVersion);
        
        const lastServerVersion = localStorage.getItem('server_version');
  vLog('🏷️ Version stockée localement:', lastServerVersion);
        
        // Ne montrer le modal que si on avait déjà une version ET qu'elle est différente
        if (lastServerVersion && lastServerVersion !== serverVersion.toString() && serverVersion) {
          vLog('🚨 VERSION SERVEUR DIFFÉRENTE DÉTECTÉE!');
          vLog('🆕 Nouvelle version serveur:', serverVersion);
          vLog('🗂️ Ancienne version stockée:', lastServerVersion);
          localStorage.setItem('server_version', serverVersion.toString());
          showUpdateModal();
          return true;
        }
        
        // Sauvegarder la version serveur si c'est la première fois (sans afficher le modal)
        if (serverVersion && !lastServerVersion) {
          vLog('💾 Première sauvegarde de la version serveur (première visite):', serverVersion);
          localStorage.setItem('server_version', serverVersion.toString());
        }
      } else {
        console.warn('❌ Erreur HTTP lors de la récupération du version.json:', versionResponse.status);
      }
    } catch (versionError) {
      console.error('❌ Erreur lors de la vérification du version.json:', versionError);
    }

    // Méthode 4: Vérifier les headers comme avant (fallback)
  vLog('🔍 Vérification des headers HTTP...');
    const mainPageResponse = await fetch(`/?v=${timestamp}&cb=${randomParam}`, {
      method: 'HEAD',
      cache: 'no-cache',
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      }
    });

    // Marquer la vérification comme effectuée
    localStorage.setItem(LAST_CHECK_KEY, Date.now().toString());

    const lastModified = mainPageResponse.headers.get('last-modified');
    const etag = mainPageResponse.headers.get('etag');
    
  vLog('🏷️ ETag actuel:', etag);
  vLog('📅 Last-Modified actuel:', lastModified);
    
    const lastEtag = localStorage.getItem('app_etag');
    const lastModifiedStored = localStorage.getItem('app_last_modified');
    
  vLog('🏷️ ETag stocké:', lastEtag);
  vLog('📅 Last-Modified stocké:', lastModifiedStored);
    
    // Ne montrer le modal que si on avait déjà un ETag ET qu'il est différent
    if (lastEtag && etag && lastEtag !== etag) {
  vLog('🚨 ETAG DIFFÉRENT DÉTECTÉ!');
      localStorage.setItem('app_etag', etag);
      showUpdateModal();
      return true;
    }
    
    // Ne montrer le modal que si on avait déjà un Last-Modified ET qu'il est différent
    if (lastModifiedStored && lastModified && lastModifiedStored !== lastModified) {
  vLog('🚨 LAST-MODIFIED DIFFÉRENT DÉTECTÉ!');
      localStorage.setItem('app_last_modified', lastModified);
      showUpdateModal();
      return true;
    }

    // Sauvegarder les nouveaux headers (sans afficher le modal pour la première visite)
    if (etag && !lastEtag) {
      vLog('💾 Première sauvegarde ETag (première visite)');
      localStorage.setItem('app_etag', etag);
    } else if (etag) {
      localStorage.setItem('app_etag', etag);
    }
    
    if (lastModified && !lastModifiedStored) {
      vLog('💾 Première sauvegarde Last-Modified (première visite)');
      localStorage.setItem('app_last_modified', lastModified);
    } else if (lastModified) {
      localStorage.setItem('app_last_modified', lastModified);
    }

    // Méthode 5: Forcer la vérification après un certain temps sans mise à jour
    const lastForceCheck = localStorage.getItem('last_force_check');
    const forceCheckInterval = 5 * 60 * 1000; // 5 minutes
    
    if (!lastForceCheck || (Date.now() - parseInt(lastForceCheck)) > forceCheckInterval) {
  vLog('⏰ Vérification forcée périodique (5 minutes écoulées)');
      localStorage.setItem('last_force_check', Date.now().toString());
      
      // Si on ne peut pas vérifier proprement, forcer le rechargement par sécurité
      if (!mainPageResponse.ok) {
    vLog('🚨 Impossible de vérifier la version, rechargement de sécurité');
        showUpdateModal();
        return true;
      }
    }

  vLog('✅ Aucune nouvelle version détectée');
    return false;
  } catch (error) {
    console.error('❌ Erreur lors de la vérification de version:', error);
    
    // En cas d'erreur répétée, forcer le rechargement après 3 échecs
    const failureCount = parseInt(localStorage.getItem('version_check_failures') || '0');
  vLog('⚠️ Échecs de vérification:', failureCount);
    
    if (failureCount > 2) {
  vLog('🚨 Trop d\'échecs de vérification, rechargement forcé');
      showUpdateModal();
      localStorage.removeItem('version_check_failures');
      return true;
    } else {
      localStorage.setItem('version_check_failures', (failureCount + 1).toString());
    }
    return false;
  }
}

export function showUpdateModal() {
  // Forcer directement le rechargement sans afficher de modal
  // pour éviter d'effrayer les utilisateurs avec un popup qui ressemble à un scam
  forceReload();
}

export function forceReload() {
  // Notification discrète pour informer l'utilisateur
  toast.loading("Mise à jour de l'application ...", {
    duration: 1500
  });
  
  // Nettoyer le localStorage des anciennes versions
  localStorage.removeItem('app_assets');
  localStorage.removeItem('app_etag');
  localStorage.removeItem('app_last_modified');
  localStorage.removeItem('last_force_check');
  localStorage.removeItem('version_check_failures');
  
  setTimeout(() => {
    // Vider tous les caches
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => {
      vLog('Suppression du cache:', name);
          caches.delete(name);
        });
      });
    }

    // Notifier le service worker de forcer la mise à jour
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then(registrations => {
        registrations.forEach(registration => {
      vLog('Mise à jour du service worker');
          registration.update();
        });
      });
      
      if (navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({ type: 'FORCE_RELOAD' });
      }
    }
    
    // Forcer le rechargement complet avec nettoyage du cache et nouveau timestamp.
    // Préserver le paramètre embed=1 si présent pour ne pas perdre le mode embed après mise à jour.
    const currentParams = new URLSearchParams(window.location.search);
    const reloadParams = new URLSearchParams();
    reloadParams.set('v', Date.now().toString());
    reloadParams.set('refresh', Math.random().toString());
    if (currentParams.get('embed') === '1' || currentParams.get('embed') === 'true') {
      reloadParams.set('embed', '1');
    }
    const newUrl = window.location.href.split('?')[0] + '?' + reloadParams.toString();

    // Retour de background mobile : le réseau peut ne pas être encore rétabli au moment
    // du focus/visibilitychange — naviguer tout de suite produit l'écran "page inaccessible".
    if (!navigator.onLine) {
      vLog('Hors ligne, attente de la reconnexion avant le rechargement forcé');
      window.addEventListener('online', () => {
        vLog('Reconnexion détectée, rechargement forcé vers:', newUrl);
        window.location.replace(newUrl);
      }, { once: true });
      return;
    }

    vLog('Rechargement forcé vers:', newUrl);
    window.location.replace(newUrl);
  }, 1500);
}

export function startVersionChecking() {
  // Arrêter le timer existant s'il y en a un
  if (versionCheckInterval) {
    clearInterval(versionCheckInterval);
  }

  // Vérification périodique toutes les 30 secondes
  versionCheckInterval = setInterval(async () => {
    await checkForNewVersion();
  }, CHECK_INTERVAL);

  // Vérification au focus de la fenêtre
  const handleFocus = async () => {
    await checkForNewVersion();
  };
  
  // Vérification lors des clics (pour détecter l'activité utilisateur)
  const handleUserActivity = async () => {
    // Vérifier seulement si la dernière vérification remonte à plus de 5 secondes
    const lastCheck = localStorage.getItem(LAST_CHECK_KEY);
    if (!lastCheck || (Date.now() - parseInt(lastCheck)) > 5000) {
      await checkForNewVersion();
    }
  };

  // Vérification lors du déplacement de la souris (moins fréquent)
  let mouseTimer: NodeJS.Timeout;
  const handleMouseMove = () => {
    clearTimeout(mouseTimer);
    mouseTimer = setTimeout(handleUserActivity, 5000); // 5 secondes après l'arrêt du mouvement
  };
  
  window.addEventListener('focus', handleFocus);
  window.addEventListener('click', handleUserActivity);
  window.addEventListener('keydown', handleUserActivity);
  window.addEventListener('mousemove', handleMouseMove);

  // Vérification avant fermeture de page
  const handleBeforeUnload = () => {
    if (versionCheckInterval) {
      clearInterval(versionCheckInterval);
    }
    clearTimeout(mouseTimer);
    window.removeEventListener('focus', handleFocus);
    window.removeEventListener('click', handleUserActivity);
    window.removeEventListener('keydown', handleUserActivity);
    window.removeEventListener('mousemove', handleMouseMove);
  };
  
  window.addEventListener('beforeunload', handleBeforeUnload);

  // Écouter les messages du service worker
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data && event.data.type === 'RELOAD_NOW') {
        forceReload();
      }
    });
  }

  // Vérification immédiate au démarrage
  setTimeout(async () => {
    await checkForNewVersion();
  }, 2000); // 2 secondes après le démarrage
  
  // Vérification supplémentaire après 10 secondes pour s'assurer qu'on a bien la dernière version
  setTimeout(async () => {
  vLog('Vérification de sécurité après 10 secondes');
    localStorage.removeItem(LAST_CHECK_KEY); // Forcer la vérification
    await checkForNewVersion();
  }, 10000);
}

export function stopVersionChecking() {
  if (versionCheckInterval) {
    clearInterval(versionCheckInterval);
    versionCheckInterval = null;
  }
}

// Fonction pour forcer une vérification manuelle
export async function forceVersionCheck(): Promise<boolean> {
  // Supprimer la limite de temps pour forcer la vérification
  localStorage.removeItem(LAST_CHECK_KEY);
  return await checkForNewVersion();
}

// Fonction de debug pour voir l'état du système de version
export function debugVersionSystem() {
  console.log('=== DEBUG SYSTÈME DE VERSION ===');
  console.log('🏷️ Version actuelle (build):', VERSION);
  console.log('🏷️ Version stockée localement:', localStorage.getItem(VERSION_KEY));
  console.log('📦 Assets stockés:', localStorage.getItem('app_assets'));
  console.log('🏷️ ETag stocké:', localStorage.getItem('app_etag'));
  console.log('📅 Last-Modified stocké:', localStorage.getItem('app_last_modified'));
  console.log('🏷️ Version serveur stockée:', localStorage.getItem('server_version'));
  console.log('⏰ Dernière vérification:', localStorage.getItem(LAST_CHECK_KEY));
  console.log('❌ Échecs de vérification:', localStorage.getItem('version_check_failures'));
  console.log('⏰ Dernière vérification forcée:', localStorage.getItem('last_force_check'));
  
  // Afficher le temps écoulé depuis la dernière vérification
  const lastCheck = localStorage.getItem(LAST_CHECK_KEY);
  if (lastCheck) {
    const elapsed = Date.now() - parseInt(lastCheck);
    console.log('⏱️ Temps écoulé depuis dernière vérification:', Math.floor(elapsed / 1000), 'secondes');
  }
  
  console.log('================================');
  console.log('💡 Pour forcer une vérification: forceVersionCheck()');
  console.log('💡 Pour forcer un rechargement: forceReload()');
  console.log('💡 Pour voir ce debug: debugVersion()');
}

// Exposer les fonctions de debug en développement
if (typeof window !== 'undefined') {
  window.debugVersion = debugVersionSystem;
  window.forceVersionCheck = forceVersionCheck;
  window.forceReload = forceReload;
  window.setVersionDebug = (enabled: boolean) => {
    window.__VERSION_DEBUG__ = enabled;
  };
  
  // Fonction de test pour forcer une mise à jour (utile pour tester)
  window.testUpdateDetection = () => {
    console.log('🧪 TEST: Simulation d\'une nouvelle version détectée');
    showUpdateModal();
  };
  
  // Fonction pour nettoyer complètement le localStorage de version
  window.clearVersionData = () => {
    console.log('🗑️ Nettoyage de toutes les données de version...');
    localStorage.removeItem(VERSION_KEY);
    localStorage.removeItem('app_assets');
    localStorage.removeItem('app_etag');
    localStorage.removeItem('app_last_modified');
    localStorage.removeItem('server_version');
    localStorage.removeItem(LAST_CHECK_KEY);
    localStorage.removeItem('version_check_failures');
    localStorage.removeItem('last_force_check');
    console.log('✅ Toutes les données de version ont été nettoyées');
    console.log('💡 La prochaine vérification sera comme une première visite');
  };
  
  if (VERSION_DEBUG || window.__VERSION_DEBUG__) {
    console.log('🛠️ Outils de debug version disponibles:');
    console.log('- debugVersion() : État du système');
    console.log('- forceVersionCheck() : Forcer une vérification');
    console.log('- forceReload() : Forcer un rechargement');
    console.log('- testUpdateDetection() : Simuler une nouvelle version');
    console.log('- clearVersionData() : Nettoyer les données stockées');
  }
}
